import * as A from 'astronomy-engine';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { services } from '../app';
import { ARABIC_INTRO, ArabicSky, STATION_WIDTH, knownMeaning, stationOf } from '../data/arabicSky';
import { plainArabic } from '../data/lore';
import { arabianReminder, quranText, stationReminder } from '../data/islam';
import { EVIDENCE } from '../data/islamEvidence';
import { Reminder } from './Reminder';
import { constellationName } from '../astro/solar';
import { starName } from '../data/catalog';
import { Vec3, norm, vecToRaDec } from '../lib/math';
import { lookingFor } from '../live/saudi';
import { place, selection, simNow } from '../state';
import { Stat } from './chrome';
import { fmtDate, fmtMag, fmtTime } from './format';
import { SayArabic, SayArabicName, SayQuran } from './SayArabic';

type HeaderFn = (title: string, subtitle: string, icon: string) => preact.JSX.Element;
interface InfoProps { header: HeaderFn; whereRow: preact.JSX.Element | null }

/** The engine's Arabic sky data, loading it on first use. */
export function useArabic() {
  const s = services.value!;
  const [, force] = useState(0);
  useEffect(() => {
    if (!s.engine.arabic) void s.engine.ensureArabic().then(() => force((x) => x + 1));
  }, []);
  return s.engine.arabic;
}

function anchorDir(sky: ArabicSky): Vec3 | null {
  const e = services.value!.engine;
  const i = e.stars.byHip.get(sky.lunar.anchorHip);
  return i === undefined ? null : e.starDir(i);
}

/** Which station the Moon is in at a given time (geocentric, as in the anwāʾ tables). */
function moonStation(sky: ArabicSky, anchor: Vec3, t: Date) {
  const v = A.GeoMoon(t);
  return stationOf(sky, anchor, norm([v.x, v.y, v.z]));
}

/** The Moon's next stay in a station: hourly steps over the coming lunar month. */
function nextVisit(sky: ArabicSky, anchor: Vec3, index: number, from: Date): { start: Date; end: Date; now: boolean } | null {
  const H = 3600e3;
  let inside = moonStation(sky, anchor, from).index === index;
  let start: Date | null = inside ? from : null;
  for (let t = from.getTime() + H; t < from.getTime() + 31 * 86400e3; t += H) {
    const k = moonStation(sky, anchor, new Date(t)).index === index;
    if (k && !inside) start = new Date(t);
    if (!k && inside && start) return { start, end: new Date(t), now: start === from };
    inside = k;
  }
  return null;
}

/** The other names the sources give for the same stars. */
function AlsoCalled({ also }: { also?: { ar: string; tr: string }[] }) {
  if (!also?.length) return null;
  return (
    <div class="arabic-also">
      Also called{' '}
      {also.map((v, k) => (
        <SayArabicName key={k} ar={v.ar} tr={v.tr} />
      ))}
    </div>
  );
}

/** The Quranic mention of the Moon's stations and a short account of the anwāʾ. */
export function ManazilNote() {
  return (
    <div class="story">
      <div class="row-label">The Moon’s stations</div>
      <p>
        The Arabs divided the Moon’s monthly path among the stars into 28 stations (manāzil), about one for each night of the month. Every 13 days or so the
        stars of one station set in the west at dawn as those of the opposite station rise in the east, and these settings and risings (the anwāʾ) served as a
        calendar of the seasons.
      </p>
      <p class="quran" lang="ar" dir="rtl">{quranText(EVIDENCE['Q-11'].arabic)}</p>
      <SayQuran text={EVIDENCE['Q-11'].arabic} />
      <p>“{EVIDENCE['Q-11'].english}” {EVIDENCE['Q-11'].ref}; see also {EVIDENCE['Q-10'].ref}</p>
      <div class="fine">
        The stations are counted from al-Sharaṭān. Their boundaries are 28 equal parts of the ecliptic, placed so that
        each part sits as closely as it can on its own station’s stars.
      </div>
    </div>
  );
}

/** A row for the Moon card: the station the Moon is in now. */
export function MoonStationRow() {
  const a = useArabic();
  if (!a) return null;
  const anchor = anchorDir(a.sky);
  if (!anchor) return null;
  const { index, frac } = moonStation(a.sky, anchor, simNow());
  const n = a.sky.lunar.names[index];
  return (
    <div class="arabic station-row">
      <button class="btn-row station-open" onClick={() => (selection.value = { kind: 'manzil', index })}>
        <div class="row-label">Lunar station {index + 1} of 28</div>
        <div class="arabic-top">
          <span class="arabic-script" lang="ar" dir="rtl">{n.ar}</span>
          <span class="arabic-tr">{n.tr}</span>
        </div>
        <div class="arabic-meaning">{knownMeaning(n.en) && `“${n.en}” · `}{frac < 0.2 ? 'just entered' : frac > 0.8 ? 'about to leave' : 'passing through'}</div>
      </button>
      <SayArabic text={n.ar} name={n.tr} />
    </div>
  );
}


/** Indigenous Arabic names of a star and the figures it belongs to (skipping a name already shown). */
export function StarArabicNames({ index, skip }: { index: number; skip?: string }) {
  const a = useArabic();
  if (!a) return null;
  const all = a.starNames.get(index) ?? [];
  const same = (n: { ar: string }) => !!skip && plainArabic(n.ar) === plainArabic(skip);
  const names = all.filter((n) => !same(n));
  // The name already shown above may still have other names worth listing.
  const others = all.filter(same).flatMap((n) => n.also ?? []);
  // A star can sit in a star group and an asterism of the same name: one button for the name.
  const figs = a.sky.figures.map((f, k) => ({ f, k })).filter(({ f, k }, i, all) => a.geo.stars[k]?.includes(index) && all.findIndex((x) => x.f.ar === f.ar && a.geo.stars[x.k]?.includes(index)) === i);
  if (!names.length && !others.length && !figs.length) return null;
  return (
    <div class="arabic">
      <div class="row-label">In the Arabian sky</div>
      {names.map((n, k) => (
        <div key={k} class="arabic-name">
          <div class="arabic-top">
            <span class="arabic-names">
              <span class="arabic-script" lang="ar" dir="rtl">{n.ar}</span>
              <span class="arabic-tr">{n.tr}</span>
            </span>
            <SayArabic text={n.ar} name={n.tr} />
          </div>
          {knownMeaning(n.en) && <div class="arabic-meaning">“{n.en}”</div>}
          <AlsoCalled also={n.also} />
        </div>
      ))}
      <AlsoCalled also={others} />
      {figs.length > 0 && (
        <div class="tag-row">
          {figs.map(({ f, k }) => (
            <button key={k} class="tag-btn" onClick={() => (selection.value = { kind: 'arabic', index: k })}>
              Part of {f.tr} <span lang="ar" dir="rtl">{f.ar}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ArabicFigureInfo({ index, header, whereRow }: InfoProps & { index: number }) {
  const s = services.value!;
  const a = useArabic();
  if (!a) return <div class="working"><div class="spinner" /></div>;
  const f = a.sky.figures[index];
  const cat = s.engine.stars;
  const members = [...(a.geo.stars[index] ?? [])].sort((x, y) => cat.mag[x] - cat.mag[y]);
  const { ra, dec } = vecToRaDec(a.geo.anchors[index]);
  const iau = constellationName(ra, dec);
  const what = f.manzil ? `lunar station ${f.manzil} of 28` : f.kind === 'constellation' ? 'Arabian star group' : 'Arabian asterism';
  return (
    <>
      {header(f.tr, `${knownMeaning(f.en) ? `${f.en} · ` : ''}${what} in ${iau}`, 'star')}
      {whereRow}
      {arabianReminder(f.id, f.tr, f.manzil) && <Reminder text={arabianReminder(f.id, f.tr, f.manzil)!} />}
      <div class="arabic">
        <div class="arabic-top">
          <span class="arabic-names">
            <span class="arabic-script big" lang="ar" dir="rtl">{f.ar}</span>
            <span class="arabic-tr">{f.tr}</span>
          </span>
          <SayArabic text={f.ar} name={f.tr} />
        </div>
        {knownMeaning(f.en) && <div class="arabic-meaning">“{f.en}”</div>}
        <AlsoCalled also={f.also} />
      </div>
      {f.manzil && (
        <div class="list">
          <button class="list-row btn-row" onClick={() => (selection.value = { kind: 'manzil', index: f.manzil! - 1 })}>
            <span>When the Moon is in this station</span>
            <span class="muted">›</span>
          </button>
        </div>
      )}
      {members.length > 0 && (
        <div class="list">
          <div class="row-label">Its stars</div>
          {members.slice(0, 10).map((i) => {
            const n = a.starNames.get(i)?.[0];
            return (
              <button class="list-row btn-row" key={i} onClick={() => (selection.value = { kind: 'star', index: i })}>
                <span>
                  {starName(cat, i)}
                  {n && <span class="muted"> · <span lang="ar" dir="rtl">{n.ar}</span></span>}
                </span>
                <span class="tnum muted">mag {fmtMag(cat.mag[i])}</span>
              </button>
            );
          })}
        </div>
      )}
      <div class="note">{ARABIC_INTRO}</div>
    </>
  );
}

export function ManzilInfo({ index, header, whereRow }: InfoProps & { index: number }) {
  const a = useArabic();
  const now = simNow();
  const hour = Math.floor(now.getTime() / 3600e3);
  const visit = useMemo(() => {
    if (!a) return null;
    const anchor = anchorDir(a.sky);
    return anchor ? nextVisit(a.sky, anchor, index, now) : null;
  }, [a, index, hour]);
  if (!a) return <div class="working"><div class="spinner" /></div>;
  const n = a.sky.lunar.names[index];
  const stars = a.sky.figures.findIndex((f) => f.manzil === index + 1);
  const sameDay = visit && fmtDate(visit.start) === fmtDate(visit.end);
  // Through the 29th nothing says where the Moon is (src/live/saudi.ts): not which station it is in, nor when.
  const looking = lookingFor(now.getTime(), place.value);
  return (
    <>
      {header(n.tr, `${knownMeaning(n.en) ? `${n.en} · ` : ''}lunar station ${index + 1} of 28`, 'moon')}
      {whereRow}
      <Reminder text={stationReminder(n.tr)} />
      <div class="arabic">
        <div class="arabic-top">
          <span class="arabic-names">
            <span class="arabic-script big" lang="ar" dir="rtl">{n.ar}</span>
            <span class="arabic-tr">{n.tr}</span>
          </span>
          <SayArabic text={n.ar} name={n.tr} />
        </div>
        {knownMeaning(n.en) && <div class="arabic-meaning">“{n.en}”</div>}
        <AlsoCalled also={n.also} />
      </div>
      {stars >= 0 && (
        <div class="list">
          <button class="list-row btn-row" onClick={() => (selection.value = { kind: 'arabic', index: stars })}>
            <span>Its stars</span>
            <span class="muted">›</span>
          </button>
        </div>
      )}
      <div class="stats">
        {!looking && <Stat
          label={visit?.now ? 'The Moon is here' : 'Moon next here'}
          value={visit ? (visit.now ? 'Now' : fmtDate(visit.start)) : 'Not found'}
          sub={visit ? (visit.now ? `until ${fmtDate(visit.end)} ${fmtTime(visit.end)}` : `${fmtTime(visit.start)} to ${sameDay ? '' : fmtDate(visit.end) + ' '}${fmtTime(visit.end)}`) : ''}
        />}
        <Stat label="Width" value={`${(Math.round(STATION_WIDTH * 10) / 10).toFixed(1)}°`} sub="of the Moon’s path" />
      </div>
      <ManazilNote />
    </>
  );
}
